import { Box, Text, Group, Progress, Stack, Badge, Divider, SimpleGrid } from '@mantine/core';
import { IconMessage, IconWorld, IconBolt, IconRobot, IconInfinity } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ActivePlanSubscription } from '@mygpt/app/users';

dayjs.extend(utc);
dayjs.extend(timezone);

interface QuotaDisplayProps {
  planSubscription: ActivePlanSubscription;
  isCorporate?: boolean;
  showAddOns?: boolean;
  addOnSubscriptions?: any[];
}

const formatNumber = (num: number | undefined | null) => {
  if (num == null || isNaN(num)) return '0';
  return num.toLocaleString();
};

const formatDate = (time?: string) => {
  if (!time) return '';
  return dayjs(time).tz('Asia/Tokyo').format('YYYY-MM-DD');
};

interface QuotaItem {
  label: string;
  used: number;
  limit: number | null;
  available: number;
  icon: React.ReactNode;
}

/**
 * 单个用量项展示
 * - 有限制：显示进度条 + 用量数字
 * - 无限制：不显示进度条，显示已用数 + 无限制标签
 */
const QuotaProgressItem = ({
  item,
  unlimitedLabel,
  availableLabel,
  usedLabel,
}: {
  item: QuotaItem;
  unlimitedLabel: string;
  availableLabel: string;
  usedLabel: string;
}) => {
  const isUnlimited = item.limit === null || item.limit === undefined;
  const percentage = isUnlimited || !item.limit ? 0 : Math.min(Math.round((item.used / item.limit) * 100), 100);

  const getProgressColor = () => {
    if (percentage >= 90) return 'red';
    if (percentage >= 70) return 'orange';
    return 'violet';
  };

  return (
    <div className="rounded-lg bg-gray-50 py-2.5 px-3">
      <Group justify="space-between" mb={isUnlimited ? 0 : 8}>
        <Group gap={8}>
          <div className="flex items-center justify-center w-7 h-7 rounded-md bg-violet-100 text-violet-600">
            {item.icon}
          </div>
          <Text size="sm" fw={500}>
            {item.label}
          </Text>
        </Group>

        {isUnlimited ? (
          <Group gap={6}>
            <Text size="sm" c="dimmed">
              {usedLabel}: {formatNumber(item.used)}
            </Text>
            <Badge
              size="sm"
              color="violet"
              variant="light"
              radius="sm"
              leftSection={<IconInfinity size={12} className="mt-px" />}
            >
              {unlimitedLabel}
            </Badge>
          </Group>
        ) : (
          <Text size="sm" fw={500} c="dark.4">
            {formatNumber(item.used)}
            <Text span c="dimmed" fw={400}>
              {' '}
              / {formatNumber(item.limit)}
            </Text>
          </Text>
        )}
      </Group>

      {!isUnlimited && (
        <>
          <Progress value={percentage} color={getProgressColor()} size={6} radius="xl" bg="gray.2" />
          <Text size="xs" c="dimmed" mt={4}>
            {availableLabel}: {formatNumber(item.available)}
          </Text>
        </>
      )}
    </div>
  );
};

/**
 * 共享的套餐用量展示组件
 * 用于个人中心和 Manage 页面
 */
export const QuotaDisplay = ({ planSubscription, isCorporate, addOnSubscriptions }: QuotaDisplayProps) => {
  const { t } = useTranslation();

  const { plan, usage } = planSubscription;

  const allQuotas: { section: string; items: QuotaItem[] }[] = [
    {
      section: t('quota.monthlyUsage'),
      items: [
        {
          label: t('quota.items.messages'),
          used: usage.message_quota?.used ?? 0,
          limit: usage.message_quota?.limit,
          available: usage.message_quota?.available ?? 0,
          icon: <IconMessage size={16} />,
        },
        {
          label: t('quota.items.webPages'),
          used: usage.web_page_quota?.used ?? 0,
          limit: usage.web_page_quota?.limit,
          available: usage.web_page_quota?.available ?? 0,
          icon: <IconWorld size={16} />,
        },
        {
          label: t('quota.items.multiModal'),
          used: usage.multi_modal_parsing_quota?.used ?? 0,
          limit: usage.multi_modal_parsing_quota?.limit,
          available: usage.multi_modal_parsing_quota?.available ?? 0,
          icon: <IconBolt size={16} />,
        },
      ],
    },
    {
      section: t('quota.totalUsage'),
      items: [
        {
          label: t('quota.items.bots'),
          used: usage.bot_quota?.used ?? 0,
          limit: usage.bot_quota?.limit,
          available: usage.bot_quota?.available ?? 0,
          icon: <IconRobot size={16} />,
        },
      ],
    },
  ];

  return (
    <Stack gap="lg">
      {/* 套餐信息头部 */}
      <div className="rounded-[10px] p-4 bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-100">
        <Group gap="sm" mb={12}>
          <Badge
            size="lg"
            color="violet"
            variant="filled"
            radius="md"
            fw={700}
            tt="none"
            styles={{ label: { fontSize: 14 } }}
          >
            {plan.name}
          </Badge>
          {isCorporate && (
            <Badge size="sm" color="grape" variant="light">
              {t('quota.corporateUser')}
            </Badge>
          )}
        </Group>
        <SimpleGrid cols={2} spacing="sm">
          <div className="rounded-lg bg-white/70 py-2 px-3">
            <Text size="xs" c="dimmed" mb={4}>
              {t('quota.price')}
            </Text>
            <Text size="sm" fw={600} c="violet.7">
              ¥{plan.price}
              <Text span size="xs" c="dimmed" fw={400}>
                {t('quota.per_month')}
              </Text>
            </Text>
          </div>
          <div className="rounded-lg bg-white/70 py-2 px-3">
            <Text size="xs" c="dimmed" mb={4}>
              {t('quota.validPeriod')}
            </Text>
            <Text size="sm" fw={500}>
              {formatDate(planSubscription.start_at)} ~ {formatDate(planSubscription.expires_at)}
            </Text>
          </div>
        </SimpleGrid>
        {plan.enable_question_tag !== undefined && (
          <div className="mt-3 rounded-lg bg-white/70 py-2 px-3 inline-flex items-center gap-2">
            <span
              className={`w-2 h-2 rounded-full shrink-0 ${plan.enable_question_tag ? 'bg-green-500' : 'bg-gray-400'}`}
            />
            <Text size="sm" fw={500} c={plan.enable_question_tag ? 'dimmed' : 'dark.6'}>
              {t('quota.enableQuestionTag')}
            </Text>
          </div>
        )}
      </div>

      {/* 用量区域 */}
      {allQuotas.map((section, sIdx) => (
        <Box key={sIdx}>
          <Divider color="gray.2" mb="md" />
          <Text size="sm" fw={600} c="dark.7" mb="sm">
            {section.section}
          </Text>
          <Stack gap={8}>
            {section.items.map((item) => (
              <QuotaProgressItem
                key={item.label}
                item={item}
                unlimitedLabel={t('quota.unlimited')}
                availableLabel={t('quota.available')}
                usedLabel={t('quota.used')}
              />
            ))}
          </Stack>
        </Box>
      ))}

      {/* 加量包 */}
      {addOnSubscriptions && addOnSubscriptions.length > 0 && (
        <Box>
          <Divider color="gray.2" mb="md" />
          <Text size="sm" fw={600} c="dark.7" mb="sm">
            {t('subscription.addon_title')}
          </Text>
          <Stack gap={8}>
            {addOnSubscriptions.map((addon, idx) => {
              const addonPercentage =
                addon.usage.limit > 0 ? Math.min(Math.round((addon.usage.used / addon.usage.limit) * 100), 100) : 0;
              return (
                <div key={idx} className="rounded-lg bg-gray-50 py-2.5 px-3">
                  <Group justify="space-between" mb={6}>
                    <Text size="sm" fw={500}>
                      {addon.add_on.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatDate(addon.start_at)} - {formatDate(addon.expires_at)}
                    </Text>
                  </Group>
                  {addon.add_on.description && (
                    <Text size="xs" c="dimmed" mb={6}>
                      {addon.add_on.description}
                    </Text>
                  )}
                  <Group gap={8} align="center">
                    <Progress
                      value={addonPercentage}
                      color={addonPercentage >= 90 ? 'red' : 'violet'}
                      size={6}
                      radius="xl"
                      bg="gray.2"
                      className="flex-1"
                    />
                    <Text size="xs" fw={500} c="dark.4" className="whitespace-nowrap">
                      {addon.usage.used}/{addon.usage.limit}
                    </Text>
                  </Group>
                </div>
              );
            })}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
